import React, { useState, useEffect, useCallback } from "react";

import { usersRef } from "../firebase";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { signInGoogle, signOff as signOut } from "../firebase";
import LoadingUser from "../components/LoadingUser";
import { GoogleAuthProvider } from "firebase/auth";
import { doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import { resetPrimaries } from "../helperFunctions/primaryCache";

const AuthContext = React.createContext(null);

const AuthProvider = (props) => {
  let [userData, setUserData] = useState({ uid: null, associatedAccounts: [] });
  let [state, setState] = useState({
    user: { uid: null },
    associatedAccounts: [],
  });
  let [accessToken, setAccessToken] = useState(null);

  //let [credential, setCredential] = useState(null);

  let [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribeFromAuth = onAuthStateChanged(auth, (user) => {
      if (user === null) {
        // If user has signed out.
        setLoading(false);
        setUserData({ uid: null, associatedAccounts: [] });
        setState({
          user: { uid: null, associatedAccounts: [] },
        });
      } else {
        // If user has signed in

        //Look in db and see if the user exists.
        let userRef = doc(usersRef, user.uid);

        getDoc(userRef)
          .then((userDoc) => {
            //If user already exists, update user object
            if (userDoc.exists()) {
              // userData is intentionally not set here - the onSnapshot listener below
              // fires immediately with this same data, and setting it in both places
              // gave userData.associatedAccounts a new array reference on each of the
              // two calls, which retriggered (and double-read) every provider keyed on
              // it. Let the listener be the sole writer.
              setState({
                user: { ...userDoc.data() },
              });
            }
            //If it is first time user has signed in.
            else {
              setDoc(userRef, {
                email: user.email,
                uid: user.uid,
                associatedAccounts: [user.uid],
              }).then(() => {
                let defaultUserData = {
                  email: user.email,
                  uid: user.uid,
                  associatedAccounts: [user.uid],
                };
                setState({ user: { ...defaultUserData } });
              });
            }
          })
          .then(() => {
            setLoading(false);
          })
          .catch((err) => {
            console.error("Unable to add user to db", err);
            setLoading(false);
            setUserData({ uid: null, associatedAccounts: [] });
          });
      }
    });

    return () => {
      unsubscribeFromAuth();
    };
  }, []);

  useEffect(() => {
    let unsubFromUser = null;
    if (state?.user?.uid) {
      //if the user has been set by auth, set up a listener on it:

      unsubFromUser = onSnapshot(doc(usersRef, state.user.uid), (snapshot) => {
        // Firestore redelivers the full document on every snapshot (e.g. a reconnect),
        // even when nothing actually changed - setUserData(snapshot.data()) unconditionally
        // handed out a brand-new object/array every time, which force-resubscribed every
        // provider keyed on userData.associatedAccounts in its effect deps (Timesheets,
        // Jobsheets, Quotes, Restores, Todos, Customers, Operatives, Rates, Templates - most
        // of the app's live listeners), each resubscribe re-reading its entire current
        // result set. Bail out of the state update entirely when the data is unchanged, so
        // those providers see a stable reference and don't needlessly re-subscribe.
        let data = snapshot.data();
        setUserData((prev) => (JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
      });
    }

    return () => {
      // if subscriber function exists, unsub
      unsubFromUser && unsubFromUser();
    };
  }, [state]);

  const signInWithGoogle = useCallback(() => {
    setState({ user: null });
    setLoading(true);
    signInGoogle().then((result) => {
      //console.log("result", result);

      if (result) {
        //If a user signs in with google, they can take advantage of google services
        //by accessing a bunch of google apis through the use of the access token. I
        //haven't set anything up yet but it is worth looking into.

        setAccessToken(GoogleAuthProvider.credentialFromResult(result));
      }
    });
  }, []);

  const signOutGoogle = useCallback(() => {
    console.log("signing out??");
    // Don't leave this account's primaries cached on the device
    resetPrimaries();
    signOut();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        signInWithGoogle,
        signOutGoogle,
        userData,
        accessToken,
      }}
    >
      {loading ? <LoadingUser /> : props.children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, AuthContext };
